import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes, UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

type RequestWithAuthorizationToken<T> = T & {
  authToken?: string | null
}

type CreateEnterpriseArg =
  | FormData
  | {
      body: FormData
      authToken?: string | null
    }

type ChangeEnterprisePasswordArg =
  | ApiTypes.Req.ChangeEnterprisePassword
  | {
      body: ApiTypes.Req.ChangeEnterprisePassword
      authToken?: string | null
    }

const getAuthorizationHeaders = (authToken?: string | null) =>
  authToken ? { Authorization: `Bearer ${authToken}` } : {}

const isCreateEnterpriseArgWithOptions = (
  arg: CreateEnterpriseArg,
): arg is { body: FormData; authToken?: string | null } => !(arg instanceof FormData)

const isChangeEnterprisePasswordArgWithOptions = (
  arg: ChangeEnterprisePasswordArg,
): arg is { body: ApiTypes.Req.ChangeEnterprisePassword; authToken?: string | null } => "body" in arg

export const enterpriseApi = createApi({
  reducerPath: "enterprises",
  tagTypes: [
    "enterprises",
    "enterprise-orders",
    "enterprise-alerts",
    "enterprise-alert-types",
    "enterprise-alert-statuses",
  ],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    createEnterprise: builder.mutation<void, CreateEnterpriseArg>({
      query: arg => {
        const body = isCreateEnterpriseArgWithOptions(arg) ? arg.body : arg
        const authToken = isCreateEnterpriseArgWithOptions(arg) ? arg.authToken : undefined

        return {
          url: `/enterprises`,
          method: "POST",
          body,
          headers: {
            Accept: "application/json",
            ...getAuthorizationHeaders(authToken),
          },
        }
      },
    }),
    updateEnterprise: builder.mutation<void, RequestWithAuthorizationToken<{ id: number; body: FormData }>>({
      query: ({ id, body, authToken }) => ({
        url: `/enterprises/${id}`,
        method: "PUT",
        body,
        headers: {
          Accept: "application/json",
          ...getAuthorizationHeaders(authToken),
        },
      }),
      invalidatesTags: ["enterprises"],
    }),
    getEnterprise: builder.query<{ data: ApiTypes.Model.Enterprise }, number>({
      query: id => ({
        url: `/enterprises/${id}`,
        method: "GET",
      }),
      providesTags: ["enterprises"],
    }),
    getEnterpriseThermostats: builder.query<ApiTypes.Res.EnterpriseThermostats, UITypes.Params>({
      query: arg => ({
        url: `/enterprises/thermostats`,
        method: "GET",
        params: prepareQueryParams(arg),
      }),
    }),
    getEnterpriseSensors: builder.query<ApiTypes.Res.EnterpriseSensorsTab, UITypes.Params>({
      query: arg => ({
        url: `/enterprises/sensors`,
        method: "GET",
        params: prepareQueryParams(arg),
        headers: { Accept: "application/json" },
      }),
    }),
    getEnterpriseOrdersNew: builder.query<ApiTypes.Res.EnterpriseOrdersNew, UITypes.Params>({
      query: arg => ({
        url: `/enterprises/orders2`,
        method: "GET",
        params: prepareQueryParams(arg),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["enterprise-orders"],
    }),
    getEnterpriseAlerts: builder.query<ApiTypes.Res.EnterpriseAlerts, UITypes.Params>({
      query: arg => ({
        url: `/enterprises/alerts`,
        method: "GET",
        params: prepareQueryParams(arg),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["enterprise-alerts"],
    }),
    getEnterpriseAlertTypesWidget: builder.query<
      { data: ApiTypes.Res.AlertTypesWidget },
      { date_start?: string; date_end?: string }
    >({
      query: params => ({
        url: `/enterprises/dashboard/widgets/alert-types`,
        method: "GET",
        params: prepareQueryParams(params),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["enterprise-alert-types"],
    }),
    getEnterpriseAlertStatusesWidget: builder.query<
      { data: ApiTypes.Res.AlertStatusesWidget },
      { date_start?: string; date_end?: string }
    >({
      query: params => ({
        url: `/enterprises/dashboard/widgets/alert-statuses`,
        method: "GET",
        params: prepareQueryParams(params),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["enterprise-alert-statuses"],
    }),
    getEnterpriseDashboardOrderSensors: builder.query<
      { data: ApiTypes.Res.EnterpriseDashboardSensorsByTypeWidget },
      void
    >({
      query: () => ({
        url: `/enterprises/dashboard/widgets/order-sensors`,
        method: "GET",
      }),
    }),
    getEnterpriseDashboardSensorStatuses: builder.query<{ data: ApiTypes.Res.SensorStatusesWidget }, void>({
      query: () => ({
        url: `/enterprises/dashboard/widgets/sensor-statuses`,
        method: "GET",
      }),
    }),
    changeEnterprisePassword: builder.mutation<void, ChangeEnterprisePasswordArg>({
      query: arg => {
        const body = isChangeEnterprisePasswordArgWithOptions(arg) ? arg.body : arg
        const authorizationToken = isChangeEnterprisePasswordArgWithOptions(arg) ? arg.authToken : undefined

        return {
          url: `/enterprises/change-password`,
          method: "POST",
          body: JSON.stringify(body),
          headers: {
            "Content-type": "application/json",
            ...getAuthorizationHeaders(authorizationToken),
          },
        }
      },
    }),
    allocateEnterpriseContractorToken: builder.mutation<
      { data: ApiTypes.Res.EnterpriseContractorDelegationToken },
      RequestWithAuthorizationToken<ApiTypes.Req.AllocateEnterpriseContractorToken>
    >({
      query: ({ contractorId, authToken }) => ({
        url: `/enterprises/contractors/${contractorId}/token`,
        method: "POST",
        headers: {
          Accept: "application/json",
          ...getAuthorizationHeaders(authToken),
        },
      }),
    }),
    deallocateEnterpriseContractorToken: builder.mutation<
      void,
      RequestWithAuthorizationToken<ApiTypes.Req.DeallocateEnterpriseContractorToken>
    >({
      query: ({ contractorId, tokenId, authToken }) => ({
        url: `/enterprises/contractors/${contractorId}/token/${tokenId}`,
        method: "DELETE",
        headers: {
          Accept: "application/json",
          ...getAuthorizationHeaders(authToken),
        },
      }),
    }),
  }),
})

export const {
  useCreateEnterpriseMutation,
  useUpdateEnterpriseMutation,
  useLazyGetEnterpriseQuery,
  useLazyGetEnterpriseThermostatsQuery,
  useLazyGetEnterpriseSensorsQuery,
  useLazyGetEnterpriseOrdersNewQuery,
  useLazyGetEnterpriseAlertsQuery,
  useLazyGetEnterpriseAlertTypesWidgetQuery,
  useLazyGetEnterpriseAlertStatusesWidgetQuery,
  useLazyGetEnterpriseDashboardOrderSensorsQuery,
  useLazyGetEnterpriseDashboardSensorStatusesQuery,
  useChangeEnterprisePasswordMutation,
  useAllocateEnterpriseContractorTokenMutation,
  useDeallocateEnterpriseContractorTokenMutation,
} = enterpriseApi
